import type {
  Operation,
  TournamentEvent,
  OperationOpen,
  OperationClosed,
  Order,
} from '../sdk'

/**
 * Extract the player ID (owner) from any operation variant.
 * Returns the playerId from the embedded order.
 */
export function getOperationOwner(operation: Operation): string {
  switch (operation.state) {
    case 'order':
      return operation.order.playerId
    case 'open':
      return operation.operationOpen.order.playerId
    case 'closed':
      return operation.operationClosed.order.playerId
  }
}

/**
 * Extract the player ID from a tournament event payload.
 * Handles all event types that carry operation data.
 */
export function getTournamentEventOwner(event: TournamentEvent): string {
  switch (event.type) {
    case 'POSITION_OPENED':
    case 'POSITION_EDITED':
      return event.data.order.playerId
    case 'POSITION_CLOSED':
      return event.data.order.playerId
    case 'ORDER_PLACED':
    case 'ORDER_EDITED':
    case 'ORDER_FAILED':
    case 'ORDER_CANCELLED':
      return event.data.playerId
    case 'TOURNAMENT_CLOSED':
      return '' // No owner for tournament closed event
  }
}

/**
 * Check if an operation belongs to the local player.
 */
export function isLocalPlayerOperation(
  operation: Operation,
  localUserId: string
): boolean {
  return getOperationOwner(operation) === localUserId
}

/**
 * Check if a tournament event belongs to the local player.
 */
export function isLocalPlayerEvent(
  event: TournamentEvent,
  localUserId: string
): boolean {
  if (event.type === 'TOURNAMENT_CLOSED') {
    return true // TOURNAMENT_CLOSED affects all players
  }
  return getTournamentEventOwner(event) === localUserId
}

/**
 * Filter an array of operations to only include local player operations.
 */
export function filterLocalPlayerOperations(
  operations: Operation[],
  localUserId: string
): Operation[] {
  return operations.filter((op) => isLocalPlayerOperation(op, localUserId))
}

/**
 * Extract the order from any operation variant.
 * Useful for accessing common order properties like symbol, side, etc.
 */
export function getOperationOrder(operation: Operation): Order {
  switch (operation.state) {
    case 'order':
      return operation.order
    case 'open':
      return operation.operationOpen.order
    case 'closed':
      return operation.operationClosed.order
  }
}
